import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { galleryFetch } from "../../../services/api/Gallery";
import PageHeader from "../../paragraphHeader";
import { ArrowRight, Folder } from "lucide-react";
import { Button } from "../../ui/button";
import NotFoundImage from "../../NotFound";
import ImageModal from "./Model";
import type { GalleryImage } from "../../../types/gallery";

interface GalleryFolder {
  name: string;
  url: string;
}

interface GalleryResponse {
  success: boolean;
  folder: string;
  folders: GalleryFolder[];
  images: GalleryImage[];
}

const GalleryPage: React.FC = () => {
  const { folder } = useParams<{ folder: string }>();

  const [folders, setFolders] = useState<GalleryFolder[]>([]);
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState("");

  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchGallery = async () => {
      if (!folder) {
        setFolders([]);
        setImages([]);
        setLoading(false);
        return;
      }

      try {
        const folderName = folder
          ? decodeURIComponent(folder).replace(/^./, (char) =>
              char.toUpperCase(),
            )
          : "";

        const response = await galleryFetch(`storage-gallery/${folderName}`);

        const data: GalleryResponse = response?.data;
        setCurrentFolder(data?.folder || " ");
        setFolders(data?.folders || []);
        setImages(data?.images || []);
      } catch (err) {
        console.error("Gallery Error:", err);
        setFolders([]);
        setImages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, [folder]);

  const hasContent = folders.length > 0 || images.length > 0;

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title={
            <h1 className="font-semibold text-[#1F4D8F] uppercase">Gallery</h1>
          }
          subtitle={
            <>
              Explore{" "}
              <span className="text-blue-500 font-bold capitalize">
                {" "}
                {folder
                  ? decodeURIComponent(folder).replace(/-/g, " ")
                  : "Explore Our Gallery"}
              </span>
            </>
          }
          paragraph="Explore Haryana's premier sports events and championships."
          backgroundImage=""
          align="center"
          showOlympicsIcon={true}
        />

        <div className="max-w-7xl mx-auto p-6">
          <div className="mb-10">
            <h2 className="text-3xl font-bold capitalize text-slate-800">
              {folder
                ? decodeURIComponent(folder).replace(/-/g, " ")
                : "Explore Our Gallery"}
            </h2>

            <div className="w-20 h-1 bg-yellow-400 rounded-full mt-3" />
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <p className="text-gray-500 text-lg">Loading...</p>
            </div>
          ) : !hasContent ? (
            <div className="flex justify-center items-center">
              <div className="w-full max-w-3xl">
                <NotFoundImage />
              </div>
            </div>
          ) : (
            <>
              {/* FOLDERS */}
              {folders.length > 0 && (
                <div className="mb-12">
                  <h3 className="text-2xl font-bold text-slate-800 mb-6">
                    Folders
                  </h3>

                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {folders.map((item) => (
                      <Link
                        key={item.name}
                        to={`/gallery/${encodeURIComponent(currentFolder)}/${encodeURIComponent(item.name)}`}
                        className="bg-white rounded-xl shadow hover:shadow-lg transition overflow-hidden group"
                      >
                        <div className="aspect-square bg-blue-50 flex items-center justify-center overflow-hidden">
                          <Folder
                            size={100}
                            strokeWidth={1.5}
                            className="text-yellow-500 group-hover:scale-110 transition"
                          />
                        </div>

                        <div className="p-0">
                          <p className="font-semibold text-xl capitalize text-center py-1">
                            {item.name.replace(/-/g, " ")}
                          </p>

                          <Button
                            size="lg"
                            className="w-full bg-yellow-500 hover:bg-yellow-700 flex items-center justify-center gap-2 rounded-none"
                          >
                            View Gallery
                            <ArrowRight size={18} />
                          </Button>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* IMAGES */}
              {images.length > 0 && (
                <div>
                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {images.map((image) => (
                      <div
                        key={image.name}
                        onClick={() => {
                          const index = images.findIndex(
                            (item) => item.name === image.name,
                          );

                          setCurrentIndex(index);
                          setSelectedImage(image);
                        }}
                        className="bg-white rounded-xl shadow hover:shadow-lg overflow-hidden group cursor-pointer"
                      >
                        <div className="aspect-square overflow-hidden">
                          <img
                            src={image.url}
                            alt={image.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                            loading="lazy"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <ImageModal
        image={selectedImage}
        images={images}
        currentIndex={currentIndex}
        onClose={() => setSelectedImage(null)}
        onNavigate={(index: number) => {
          setCurrentIndex(index);
          setSelectedImage(images[index]);
        }}
      />
    </>
  );
};

export default GalleryPage;
